import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import './App.css';
import './i18n.js';

import CookieBanner from './cookiebanner/CookieBanner';
import CookieSettingsButton from './cookiebanner/CookieSettingsButton';

import PageLoader from './pageloader/pageloader';

import FloatingButtons from "./floatingbuttons/floatingbuttons";
import Navbar from './hompagecomponents/navbar.js';
import Footer from './hompagecomponents/footer.js';

const Home = lazy(() => import('./hompagecomponents/homepagemain/homepagemain'));
const Aboutus = lazy(() => import('./aboutpagecomponents/aboutpagemain/aboutpagemain'));
const Services = lazy(() => import("./servicepagecomponents/servicepagemain/servicepagemain"));
const Cozumler = lazy(() => import("./solutions/cozumlerimiz"));
const Contact = lazy(() => import("./contactpage/contactpagemain/contactpagemain"));
const FAQ = lazy(() => import("./faqcomponents/faqcomponentsmain/faqcomponentsmain"));
const Project = lazy(() => import("./projectpagecomponents/projectpagemain/projectpagemain"));
const Careerpage = lazy(() => import("./careerpage/Careerpage"));
const Jobapply1 = lazy(() => import("./careerpage/Applyjob1/Applyjob1"));
const Jobapply2 = lazy(() => import("./careerpage/Applyjob1/Applyjob2"));
const BlogPage = lazy(() => import("./blogpage/blogpage"));
const PackagesPage = lazy(() => import("./packages/packages"));

const Cerezler = lazy(() => import("./yasalverigüvenligi/cerezler"));
const Gizlilikpolitikasi = lazy(() => import("./yasalverigüvenligi/gizlilikpolitikasi"));
const Kullanimsartlari = lazy(() => import("./yasalverigüvenligi/kullanimsartlari"));
const Verigüvenligi = lazy(() => import("./yasalverigüvenligi/verigüvenligi"));
const Kunye = lazy(() => import("./yasalverigüvenligi/kunye"));

const Strategydanismanlik = lazy(() => import("./stratejivedanismanlik/strategyvedanismanlikmain"));
const Operasyondestek = lazy(() => import("./operasyondestekpage/operasyondestekmain"));
const SoftwareDevelopment = lazy(() => import("./yazilimgelistirme/yazilimgelistirmemain"));
const MicrosoftCözümleri = lazy(() => import("./microsoftcözümleri/microsoftcözümlerimain"));
const DevopsandCloud = lazy(() => import("./devopsvebulut/devopsvebulutmain"));
const CorporateSolutions = lazy(() => import("./corporatesolutions/corporatesolutionsmain"));
const DataandAnalitics = lazy(() => import("./dataandanalitics/dataandanaliticsmain"));
const IoTandIndustry = lazy(() => import("./iotnadindustry/iotnadindustrymain"));
const EducationHRSolutions = lazy(() => import("./educationHRsolutions/educationHRsolutionsmain"));
const CyberSecurity = lazy(() => import("./cybersecurity/cybersecuritymain"));
const OtherSolutions = lazy(() => import("./othersolutions/othersolutionsmain"));

const LEGAL_PAGE_PATHS = [
  '/cerez-politikamiz',
  '/gizlilik-politikamiz',
  '/kullanim-sartlarimiz',
  '/veri-güvenligi-politikamiz',
  '/kunye-bilgilerimiz'
];

const NavigationWrapper = ({ children }) => {
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);

  useEffect(() => {
    setIsLoading(true);
    setLoadingProgress(10);
    
    const progressInterval = setInterval(() => {
      setLoadingProgress(prev => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return 90;
        }
        return prev + 20;
      });
    }, 250);

    const timer = setTimeout(() => {
      clearInterval(progressInterval);
      setLoadingProgress(100);
      
      const hideTimer = setTimeout(() => {
        setIsLoading(false);
        setLoadingProgress(0);
      }, 200);
      
      return () => clearTimeout(hideTimer);
    }, 400);

    return () => {
      clearTimeout(timer);
      clearInterval(progressInterval);
    };
  }, [location.pathname]);

  return (
    <>
      {isLoading && <PageLoader progress={loadingProgress} />}
      {children}
    </>
  );
};

const FallbackLoader = () => (
  <div className="minimal-loader">
    <div className="minimal-spinner"></div>
  </div>
);

function AppContent() {
  const location = useLocation();
  const [showCookieBanner, setShowCookieBanner] = useState(false);
  
  const isLegalPage = LEGAL_PAGE_PATHS.includes(location.pathname);

  const openCookieSettings = () => {
    setShowCookieBanner(true);
  };

  const closeCookieBanner = () => {
    setShowCookieBanner(false);
  };

  return (
    <div className="App">
      <Navbar />
      <NavigationWrapper>
        <Suspense fallback={<FallbackLoader />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/hakkimizda" element={<Aboutus />} />
            <Route path="/hizmet-alanlarimiz" element={<Services />} />
            <Route path="/cozumlerimiz" element={<Cozumler />} />
            <Route path="/bizimle-iletisime-gecin" element={<Contact />} />
            <Route path="/sikca-sorulan-sorular-sss" element={<FAQ />} />
            <Route path="/son-tamamlanan-projelerimiz" element={<Project />} />
            <Route path="/sizin-kariyeriniz" element={<Careerpage />} />
            <Route path="/blog-sayfamiz" element={<BlogPage />} />
            <Route path="/paketlerimiz" element={<PackagesPage />} />

            <Route path="/strategyandconsultant" element={<Strategydanismanlik />} />
            <Route path="/operationandsupport" element={<Operasyondestek />} />
            <Route path="/softwaredevelopment" element={<SoftwareDevelopment />} />
            <Route path="/microsoft365solutions" element={<MicrosoftCözümleri />} />
            <Route path="/devopsandcloud" element={<DevopsandCloud />} />
            <Route path="/corporatesolutions" element={<CorporateSolutions />} />
            <Route path="/dataandanaliticssolutions" element={<DataandAnalitics />} />
            <Route path="/iotsolutions" element={<IoTandIndustry />} />
            <Route path="/educationhrsolutions" element={<EducationHRSolutions />} />
            <Route path="/cybersecuritysolutions" element={<CyberSecurity />} />
            <Route path="/yapayzekaveotomasyon" element={<OtherSolutions />} />

            <Route path="/cerez-politikamiz" element={<Cerezler />} />
            <Route path="/gizlilik-politikamiz" element={<Gizlilikpolitikasi />} />
            <Route path="/kullanim-sartlarimiz" element={<Kullanimsartlari />} />
            <Route path="/veri-güvenligi-politikamiz" element={<Verigüvenligi />} />
            <Route path="/kunye-bilgilerimiz" element={<Kunye />} />

            <Route path="/job-ürüntassarimcisi" element={<Jobapply1 />} />
            <Route path="/job-markatasarimcisi" element={<Jobapply2 />} />
          </Routes>
        </Suspense>
      </NavigationWrapper>
      
      <Footer />
      <FloatingButtons />
      {!isLegalPage && <CookieSettingsButton onOpenSettings={openCookieSettings} />}
      {showCookieBanner && <CookieBanner onClose={closeCookieBanner} isOpenFromButton={true} />}
      {!isLegalPage && !showCookieBanner && <CookieBanner onClose={closeCookieBanner} isOpenFromButton={false} />}
    </div>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;